import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

const BOOKMAKER_DISPLAY_NAMES = {
    'sportsbet': 'Sportsbet',
    'tab': 'TAB',
    'pointsbet': 'Pointsbet',
    'neds': 'Neds',
    'betright': 'Betright',
    'bet365': 'Bet365',
    'betmakers': 'Betmakers',
    'ladbrokes': 'Ladbrokes',
    'dabble': 'Dabble',
    'tabtouch': 'TabTouch',
    'unibet': 'Unibet',
    'betr': 'Betr',
    'bluebet': 'BlueBet',
};

const LINKED_BOOKMAKERS = {
    'tabtouch': 'unibet',
    'neds': 'ladbrokes',
};

const MIRRORED_ODDS = Object.entries(LINKED_BOOKMAKERS).reduce((acc, [src, mirror]) => {
    acc[mirror] = src;
    return acc;
}, {});

function RawOddsTrackerModal({ entry, onClose, bookmakerLogos, unitSize = 20, onAddToTracker, isAddingBet }) {
    const [selectedBookmaker, setSelectedBookmaker] = useState(null);
    const [stake, setStake] = useState('');
    const stakeInputRef = useRef(null);

    const oddsEntries = useMemo(() => {
        const base = Object.entries(entry.odds);
        const expanded = [...base];
        base.forEach(([bk, price]) => {
            const mirror = LINKED_BOOKMAKERS[bk];
            if (mirror && !entry.odds[mirror]) {
                expanded.push([mirror, price]);
            }
            const source = MIRRORED_ODDS[bk];
            if (source && !entry.odds[source]) {
                expanded.push([source, price]);
            }
        });
        return expanded;
    }, [entry.odds]);
    const bestOdds = oddsEntries.length > 0 ? Math.max(...oddsEntries.map(([, v]) => v)) : null;
    const bestBookmaker = oddsEntries.find(([, v]) => v === bestOdds)?.[0] || null;

    useEffect(() => {
        if (bestBookmaker) setSelectedBookmaker(bestBookmaker);
    }, [bestBookmaker]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const timer = setTimeout(() => stakeInputRef.current?.focus(), 50);
        return () => {
            document.body.style.overflow = '';
            clearTimeout(timer);
        };
    }, []);

    if (!entry) return null;

    const selectedPrice = selectedBookmaker
        ? (entry.odds[selectedBookmaker] ?? oddsEntries.find(([bk]) => bk === selectedBookmaker)?.[1] ?? null)
        : null;
    const stakeNum = parseFloat(stake);
    const hasPayout = stake && !isNaN(stakeNum) && stakeNum > 0 && selectedPrice;
    const payout = hasPayout ? (stakeNum * selectedPrice).toFixed(2) : null;

    const formatOdds = (odds) => {
        if (odds === null || odds === undefined) return '-';
        return Number(odds).toFixed(2);
    };

    const renderBookmakerLogo = (bookmaker, size = 24) => {
        const logo = bookmakerLogos?.[bookmaker];
        const name = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;
        if (logo) {
            return <img src={logo} alt={name} style={{ width: size, height: size }} className="rounded-sm object-cover" title={name} />;
        }
        return <span className="text-[9px] font-mono text-slate-500 uppercase tracking-wider" title={name}>{name.substring(0, 3)}</span>;
    };

    const handleSubmit = async () => {
        if (!onAddToTracker || !selectedBookmaker || !selectedPrice) return;
        const stakeVal = parseFloat(stake);
        if (!stakeVal || stakeVal <= 0) return;

        const d = entry.startTime ? new Date(entry.startTime) : new Date();
        const betDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const bookie = BOOKMAKER_DISPLAY_NAMES[selectedBookmaker] || selectedBookmaker;

        await onAddToTracker({
            date: betDate,
            sport: 'AFL',
            game: entry.match,
            bet: `${entry.player} ${entry.market}`,
            odds: selectedPrice,
            stake: stakeVal,
            payout: parseFloat((stakeVal * selectedPrice).toFixed(2)),
            bookie: bookie,
            result: 'pending',
            pl: 0,
            bonus_bet: false,
        });
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-slate-900 border border-slate-700/50 rounded-xl w-full max-w-md mx-4 shadow-2xl overflow-hidden animate-slide-up">
                {/* Header bar */}
                <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <h3 className="text-sm font-semibold text-white truncate tracking-tight">{entry.player}</h3>
                        <div className="w-px h-4 bg-slate-700/50 flex-shrink-0" />
                        <span className="text-xs font-mono text-slate-400 truncate" title={entry.market}>{entry.market}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-800 transition-all ml-3 flex-shrink-0"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Match + selected price readout */}
                <div className="px-4 py-3 border-b border-slate-700/30">
                    <div className="text-[11px] font-mono text-slate-600 mb-2">{entry.match}</div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            {selectedBookmaker && renderBookmakerLogo(selectedBookmaker, 22)}
                            <span className="text-xs text-slate-400 font-medium">
                                {BOOKMAKER_DISPLAY_NAMES[selectedBookmaker] || selectedBookmaker}
                            </span>
                            {selectedPrice === bestOdds && (
                                <span className="px-1.5 py-0.5 text-[8px] font-mono font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded uppercase">Best</span>
                            )}
                        </div>
                        <span className="text-2xl font-bold text-emerald-400 font-mono tabular-nums tracking-tight">{formatOdds(selectedPrice)}</span>
                    </div>
                </div>

                {/* Bookmaker selection grid */}
                <div className="px-4 py-3">
                    <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest mb-2">Select Bookmaker</div>
                    <div className="grid grid-cols-4 gap-1.5">
                        {oddsEntries
                            .sort(([, a], [, b]) => b - a)
                            .map(([bk, price]) => {
                                const isSelected = bk === selectedBookmaker;
                                const isBest = price === bestOdds;
                                return (
                                    <button
                                        key={bk}
                                        onClick={() => setSelectedBookmaker(bk)}
                                        className={`flex flex-col items-center gap-1 py-2 px-1 rounded transition-all border ${
                                            isSelected
                                                ? 'bg-emerald-500/10 border-emerald-500/30'
                                                : 'border-slate-700/30 hover:bg-slate-800/50 hover:border-slate-600/50'
                                        }`}
                                    >
                                        {renderBookmakerLogo(bk, 22)}
                                        <span className={`text-[12px] font-mono font-semibold tabular-nums ${
                                            isSelected ? 'text-emerald-400' : isBest ? 'text-emerald-400/60' : 'text-slate-400'
                                        }`}>
                                            {formatOdds(price)}
                                        </span>
                                    </button>
                                );
                            })}
                    </div>
                </div>

                {/* Stake input + unit buttons */}
                <div className="px-4 py-3 border-t border-slate-700/30">
                    <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest mb-2">Stake</div>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-mono">$</span>
                            <input
                                ref={stakeInputRef}
                                type="number"
                                step="0.01"
                                min="0"
                                value={stake}
                                onChange={(e) => setStake(e.target.value)}
                                placeholder="0.00"
                                className="w-full pl-6 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 transition-colors"
                            />
                        </div>
                        <div className="flex gap-1.5">
                            {[{ label: '1U', mult: 1 }, { label: '0.5U', mult: 0.5 }, { label: '0.2U', mult: 0.2 }].map((unit) => {
                                const unitVal = (unitSize * unit.mult).toFixed(2);
                                return (
                                    <button
                                        key={unit.label}
                                        onClick={() => setStake(unitVal)}
                                        className={`px-3 py-2 rounded text-xs font-mono font-medium transition-all border ${
                                            stake === unitVal
                                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        {unit.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Payout readout */}
                    {hasPayout && (
                        <div className="flex items-center justify-between mt-3 py-2.5 px-3 bg-emerald-500/5 border border-emerald-500/15 rounded">
                            <div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Payout</div>
                                <div className="text-sm font-mono font-bold text-emerald-400 tabular-nums">${payout}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="px-4 py-3 border-t border-slate-700/30 flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 text-xs font-mono font-medium text-slate-400 hover:text-white rounded border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isAddingBet || !hasPayout}
                        onClick={handleSubmit}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded text-xs font-mono font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                        {isAddingBet ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Adding...
                            </span>
                        ) : 'Add to Tracker'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default RawOddsTrackerModal;
